/**
 * GA4 DataLayer - Event Handlers
 * REQ-012: Tracking Architecture v2
 *
 * Handles events the Google & YouTube App cannot provide:
 * - view_cart: Fires when cart drawer opens (MutationObserver on sidebar)
 * - select_item: Fires when product card is clicked on collection pages
 *
 * Dependencies:
 * - dataLayer must be initialized (by ga4-datalayer-init.liquid)
 * - Cart sidebar must have id="site-cart-sidebar" (theme.liquid)
 * - Product cards must have data-ga4-* attributes (product-item.liquid)
 */

/* global MutationObserver */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  /**
   * Helper: Map cart.js response to GA4 items array
   * @param {Array} items - Cart items from /cart.js
   * @returns {Array} GA4-formatted items
   */
  function mapCartItems(items) {
    return items.map(function (item) {
      return {
        item_id: String(item.variant_id),
        item_name: item.product_title,
        item_brand: item.vendor || '',
        item_category: item.product_type || '',
        item_variant: item.variant_title || 'Default Title',
        price: item.final_price / 100,
        quantity: item.quantity
      };
    });
  }

  /**
   * Helper: Fetch current cart data from Shopify
   * @returns {Promise<Object>} Cart data
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  /**
   * view_cart Event Handler
   * REQ-012: FR-008
   *
   * Fires when the cart drawer opens
   * Uses MutationObserver to detect when sidebar becomes visible
   */
  function initViewCartTracking() {
    var cartSidebar = document.getElementById('site-cart-sidebar');
    if (!cartSidebar) {
      return;
    }

    var hasFiredForThisOpen = false;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          var isOpen = cartSidebar.classList.contains('sidebar--opened');

          if (isOpen && !hasFiredForThisOpen) {
            hasFiredForThisOpen = true;
            fireViewCartEvent();
          } else if (!isOpen) {
            hasFiredForThisOpen = false;
          }
        }
      });
    });

    observer.observe(cartSidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Fire the view_cart event
   * Fetches fresh cart data to ensure accuracy
   */
  function fireViewCartEvent() {
    fetchCart().then(function (cart) {
      if (cart.item_count === 0) {
        return;
      }

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: cart.currency,
          value: cart.total_price / 100,
          items: mapCartItems(cart.items)
        }
      });
    });
  }

  /**
   * select_item Event Handler
   * REQ-012: FR-007
   *
   * Fires when a product card is clicked on a collection page.
   * Reads product data from data-ga4-* attributes on the card element.
   * Uses event delegation on the collection grid for efficiency.
   */
  function initSelectItemTracking() {
    var grid = document.getElementById('main-collection-product-grid');
    if (!grid) {
      return;
    }

    grid.addEventListener('click', function (event) {
      var card = event.target.closest('[data-ga4-item-id]');
      if (!card) {
        return;
      }

      var itemId = card.getAttribute('data-ga4-item-id');
      var itemName = card.getAttribute('data-ga4-item-name');
      var itemBrand = card.getAttribute('data-ga4-item-brand');
      var itemCategory = card.getAttribute('data-ga4-item-category');
      var itemPrice = parseFloat(card.getAttribute('data-ga4-item-price') || 0);
      var itemIndex = parseInt(card.getAttribute('data-ga4-index') || 0, 10);
      var listId = card.getAttribute('data-ga4-list-id');
      var listName = card.getAttribute('data-ga4-list-name');

      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: listId,
          item_list_name: listName,
          items: [
            {
              item_id: itemId,
              item_name: itemName,
              item_brand: itemBrand,
              item_category: itemCategory,
              price: itemPrice,
              index: itemIndex
            }
          ]
        }
      });
    });
  }

  /**
   * Initialize all tracking
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initViewCartTracking();
        initSelectItemTracking();
      });
    } else {
      initViewCartTracking();
      initSelectItemTracking();
    }
  }

  init();
})();
